import { useState, useCallback, useRef } from 'react';
import { useParams } from '@tanstack/react-router';
import { useToast } from '@/context/ToastContext';
import {
  EntityDetailTabsProvider,
  EntityDetailTabsHeader,
  EntityDetailTabsContent,
  useEntityDetailTabsContext,
} from '@/components/accounts-payable/shared/entity-detail-tabs';
import { SystemEntity } from '@erp/shared-types';
import { Tabs } from '@/components/ui/tabs';
import { ForegroundAgentChat } from '@/components/common/ForegroundAgentChat';
import {
  type ComparisonData,
  type ChartData,
} from '@/components/accounts-payable/InvoiceComparisonDialog';
import { InvoiceComparisonPanel } from '@/components/accounts-payable/invoice-detail/InvoiceComparisonPanel';
import { EmailDraftEditor } from '@/components/email-draft-editor/EmailDraftEditor';
import { useEmailDraft } from '@/api/communications/useEmailDraft';

function InvoiceDetailTabsWrapper() {
  const { activeTabId, setActiveTabId } = useEntityDetailTabsContext();
  return (
    <Tabs
      value={activeTabId}
      onValueChange={setActiveTabId}
      className="flex flex-col flex-1 min-h-0"
    >
      <div className="shrink-0 grow-0">
        <EntityDetailTabsHeader />
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        <EntityDetailTabsContent />
      </div>
    </Tabs>
  );
}

function InvoiceDetailPageContent({ invoiceId }: { invoiceId: string }) {
  const { showToast } = useToast();

  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(
    null,
  );
  const [comparisonChart, setComparisonChart] = useState<ChartData | null>(
    null,
  );
  const [showChartTab, setShowChartTab] = useState(false);
  const [comparisonDefaultTab, setComparisonDefaultTab] = useState<
    'table' | 'chart'
  >('table');
  const comparisonPanelRef = useRef<HTMLDivElement>(null);

  const [emailDraftEditorOpen, setEmailDraftEditorOpen] = useState(false);
  const [emailDraftId, setEmailDraftId] = useState<string | null>(null);

  const { draft: emailDraft } = useEmailDraft(emailDraftId ?? undefined);

  const handleShowComparison = useCallback(
    (payload: {
      comparisonData: ComparisonData;
      chart: ChartData;
      defaultTab?: 'table' | 'chart';
    }) => {
      setComparisonData(payload.comparisonData);
      setComparisonChart(payload.chart);
      setShowChartTab(true);
      setComparisonDefaultTab(payload.defaultTab ?? 'table');
      setTimeout(() => {
        comparisonPanelRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    },
    [],
  );

  const handleOpenEmailDraft = useCallback((draftId: string) => {
    setEmailDraftId(draftId);
    setEmailDraftEditorOpen(true);
  }, []);

  const handleEmailDraftSave = useCallback(async () => {
    // TODO: Implement save functionality
  }, []);

  const handleEmailDraftSend = useCallback(async () => {
    showToast({
      title: 'Email sent',
      message: 'Your email has been sent successfully.',
      color: 'success',
    });
    setEmailDraftEditorOpen(false);
    setEmailDraftId(null);
  }, [showToast]);

  const handleEmailDraftDiscard = useCallback(() => {
    setEmailDraftEditorOpen(false);
    setEmailDraftId(null);
  }, []);

  return (
    <>
      <div className="flex flex-1 min-h-0">
        <div className="flex-1 min-h-0 overflow-hidden">
          <EntityDetailTabsProvider
            initialEntity={{
              entityType: SystemEntity.AP_INVOICE,
              entityId: invoiceId,
            }}
          >
            <InvoiceDetailTabsWrapper />
          </EntityDetailTabsProvider>
        </div>

        <ForegroundAgentChat
          moduleKey="ap"
          currentPage={`/accounts-payable/invoices/${invoiceId}`}
          isEmbedded={true}
          initialMessage="Analyze this invoice for discrepancies"
          onShowComparison={handleShowComparison}
          onOpenEmailDraft={handleOpenEmailDraft}
        />
      </div>

      {comparisonData && comparisonChart && (
        <div ref={comparisonPanelRef} className="mt-6">
          <InvoiceComparisonPanel
            comparisonData={comparisonData}
            chart={comparisonChart}
            chartVisible={showChartTab}
            defaultTab={comparisonDefaultTab}
          />
        </div>
      )}

      {emailDraft && (
        <EmailDraftEditor
          draft={{
            id: emailDraft.id,
            to: emailDraft.to,
            cc: emailDraft.cc,
            subject: emailDraft.subject,
            content: emailDraft.content,
            htmlContent: emailDraft.htmlContent,
            isAiGenerated: emailDraft.isAiGenerated,
            status: emailDraft.status,
            sendingMailbox: emailDraft.sendingMailbox,
          }}
          mode="markdown"
          open={emailDraftEditorOpen}
          onOpenChange={setEmailDraftEditorOpen}
          onSave={handleEmailDraftSave}
          onSend={handleEmailDraftSend}
          onDiscard={handleEmailDraftDiscard}
        />
      )}
    </>
  );
}

const InvoiceDetailPage: React.FC = () => {
  const { invoiceId } = useParams({
    from: '/authenticated/accounts-payable/invoices/$invoiceId',
  });

  return <InvoiceDetailPageContent invoiceId={invoiceId} />;
};

export default InvoiceDetailPage;
